import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";

import HttpService from "../../../../../services/http";
import { SEND_MESSAGE_TYPE } from "../../../../../constants/types";
import { SendContext, PreviewFields } from "./sendContext";

export interface RootSendContextArgs {
  rootBasePath: string;
  type: SEND_MESSAGE_TYPE;
  toSendMsgUserId: string;
  rootEntityIdentifications: string[];
  show?: boolean;
  singleEmailPath?: string;
}

export const useRootSendContext = (args: RootSendContextArgs): SendContext => {
  const {
    rootBasePath,
    type,
    toSendMsgUserId,
    rootEntityIdentifications,
    show,
    singleEmailPath,
  } = args;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const recipientCount =
    type === SEND_MESSAGE_TYPE.SINGLE ? 1 : rootEntityIdentifications.length;

  const { data: favData } = useQuery(
    ["rootEmailFavorites", rootBasePath],
    async () => {
      const res = await HttpService.get(
        `${rootBasePath}/system/email-favorites`
      );
      return res?.data?.element?.user_data_preferences ?? {};
    },
    { enabled: Boolean(show && rootBasePath) }
  );

  const favorites = useMemo(
    () =>
      favData
        ? {
            intro: favData.fav_intro as string | undefined,
            signature: favData.fav_signature as string | undefined,
            template: favData.fav_template as string | undefined,
          }
        : undefined,
    [favData]
  );

  const submit: SendContext["submit"] = async (formData, { onSuccess }) => {
    setIsSubmitting(true);
    try {
      if (type === SEND_MESSAGE_TYPE.SINGLE) {
        await HttpService.post(
          singleEmailPath ??
            `${rootBasePath}/system/entity/${toSendMsgUserId}/email`,
          formData
        );
        toast.success(
          singleEmailPath ? "Email sent." : "Email sent to the owner."
        );
      } else {
        formData.set(
          "entity_identifications",
          JSON.stringify(rootEntityIdentifications)
        );
        const res = await HttpService.post(
          `${rootBasePath}/system/owners/email`,
          formData
        );
        const sent = res?.data?.element?.sent?.length ?? 0;
        const failed = res?.data?.element?.failed?.length ?? 0;
        if (failed > 0) {
          toast.warning(`Email sent to ${sent} owners, ${failed} failed.`);
        } else {
          toast.success(`Email sent to ${sent} owners.`);
        }
      }
      onSuccess();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to send the email."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const preview = async (fields: PreviewFields): Promise<string> => {
    setIsPreviewing(true);
    try {
      const response = await HttpService.post(
        `${rootBasePath}/system/email-preview`,
        {
          subject: fields.subject,
          content: fields.contentHtml,
          intro: fields.intro,
          signature: fields.signatureHtml,
          template: fields.template,
          ...(type === SEND_MESSAGE_TYPE.SINGLE &&
          toSendMsgUserId &&
          !singleEmailPath
            ? { entity_identification: toSendMsgUserId }
            : {}),
        }
      );
      const data = response?.data;
      return typeof data === "string"
        ? data
        : data?.element?.html ?? data?.html ?? "";
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to render the preview."
      );
      throw error;
    } finally {
      setIsPreviewing(false);
    }
  };

  const correct = async (html: string): Promise<string | null> => {
    try {
      const res = await HttpService.post(
        `${rootBasePath}/system/correct-message`,
        { message: html }
      );
      return res?.data?.element?.answer ?? null;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to check the message."
      );
      return null;
    }
  };

  const saveDefault: SendContext["saveDefault"] = async (key, value) => {
    if (!value) return;
    try {
      await HttpService.post(`${rootBasePath}/system/email-favorites/${key}`, {
        value,
      });
      toast.success("Saved as default.");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to save as default."
      );
    }
  };

  return {
    recipientCount,
    canSend: true,
    isSubmitting,
    isPreviewing,
    accessDenied: false,
    resolvesIntroServerSide: true,
    favorites,
    banners: null,
    modals: null,
    accessDeniedModal: null,
    submit,
    preview,
    correct,
    saveDefault,
  };
};
